/**
 * OffertTabelle — the sortable/filterable table view of Tab 4a (P2 Placement Slice 10). One flat row
 * per offer across all groups; client-side sort (default Eingang DESC) + an IDP-Status filter row.
 * Same action set as the Kachel (gated on canWrite && aktionErlaubt && bearbeitbar).
 */

import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { FilterChip } from '../../components/ui/FilterChip';
import { OffertStatusBadge } from './OffertStatusBadge';
import { Button } from '../../components/ui/Button';
import { chf, dateTimeShortDe } from '../../lib/format';
import {
  OFFER_IDP_STATUS,
  OFFER_IDP_STATUS_LABELS,
  OFFER_NICHT_ZUGEORDNET_LABEL,
} from '@brokly/shared';
import type { OfferEintrag, OfferSubmissionGruppe } from '@brokly/shared';

type SortKey = 'versicherer' | 'nummer' | 'eingang' | 'version' | 'status' | 'idp' | 'konfidenz' | 'praemie';
type Filter = 'alle' | 'zu_pruefen' | 'nicht_zugeordnet' | 'fehlgeschlagen';

type Row = { item: OfferEintrag; gruppe: OfferSubmissionGruppe };

export function OffertTabelle({
  gruppen,
  canWrite,
  aktionErlaubt,
  onOpen,
  onZuordnen,
  onAktuell,
  onReExtract,
  onNormalisieren,
  onVerwerfen,
}: {
  gruppen: OfferSubmissionGruppe[];
  canWrite: boolean;
  aktionErlaubt: boolean;
  onOpen: (item: OfferEintrag) => void;
  onZuordnen: (item: OfferEintrag) => void;
  onAktuell: (item: OfferEintrag) => void;
  onReExtract: (item: OfferEintrag) => void;
  onNormalisieren: (item: OfferEintrag) => void;
  onVerwerfen: (item: OfferEintrag) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'eingang', dir: 'desc' });
  const [filter, setFilter] = useState<Filter>('alle');

  const allRows: Row[] = useMemo(() => gruppen.flatMap((g) => g.items.map((item) => ({ item, gruppe: g }))), [gruppen]);

  const filtered = allRows.filter(({ item }) => {
    if (filter === 'zu_pruefen') return item.idp_status === OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN;
    if (filter === 'nicht_zugeordnet') return item.submission_id === null;
    if (filter === 'fehlgeschlagen') return item.idp_status === OFFER_IDP_STATUS.FEHLGESCHLAGEN;
    return true;
  });

  const rows = [...filtered].sort((a, b) => {
    const mul = sort.dir === 'asc' ? 1 : -1;
    const valOf = (r: Row): string | number => {
      switch (sort.key) {
        case 'versicherer': return r.item.carrier_name ?? r.gruppe.name ?? '';
        case 'nummer': return r.item.submission_number ?? '';
        case 'eingang': return r.item.received_at;
        case 'version': return r.item.version_no ?? 0;
        case 'status': return r.item.status;
        case 'idp': return r.item.idp_status;
        case 'konfidenz': return r.item.extractor_confidence ?? -1;
        case 'praemie': return r.item.normalized?.praemie.jahr ?? -1;
      }
    };
    const av = valOf(a);
    const bv = valOf(b);
    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
    return 0;
  });

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const filters: Array<{ value: Filter; label: string }> = [
    { value: 'alle', label: 'Alle' },
    { value: 'zu_pruefen', label: 'Zu pruefen' },
    { value: 'nicht_zugeordnet', label: OFFER_NICHT_ZUGEORDNET_LABEL },
    { value: 'fehlgeschlagen', label: 'Fehlgeschlagen' },
  ];

  const cols: Array<{ key: SortKey; label: string }> = [
    { key: 'versicherer', label: 'Versicherer' },
    { key: 'nummer', label: 'Submissions-Nummer' },
    { key: 'eingang', label: 'Eingang' },
    { key: 'version', label: 'Version' },
    { key: 'status', label: 'Status' },
    { key: 'idp', label: 'IDP-Status' },
    { key: 'konfidenz', label: 'Konfidenz' },
    { key: 'praemie', label: 'Praemie/Jahr' },
  ];

  return (
    <div className="flex flex-col gap-3" data-testid="offert-tabelle">
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <FilterChip
            key={f.value}
            label={f.label}
            active={filter === f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`offert-filter-${f.value}`}
          />
        ))}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {cols.map((c) => (
                <TableHead key={c.key}>
                  <button type="button" className="font-medium hover:underline" onClick={() => toggleSort(c.key)} data-testid={`offert-sort-${c.key}`}>
                    {c.label}
                    {sort.key === c.key ? (sort.dir === 'asc' ? ' ^' : ' v') : ''}
                  </button>
                </TableHead>
              ))}
              <TableHead>Datei</TableHead>
              <TableHead>Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ item, gruppe }) => {
              const verworfen = item.verworfen_at !== null;
              const unassigned = item.submission_id === null;
              const usable = item.idp_status !== OFFER_IDP_STATUS.KEINE_OFFERTE && !verworfen;
              const showActions = canWrite && aktionErlaubt && item.bearbeitbar;
              return (
                <TableRow key={item.id} data-testid={`offert-row-${item.id}`} className={verworfen || (item.abgeloest_at !== null && !item.is_current) ? 'opacity-60' : ''}>
                  <TableCell>{unassigned ? OFFER_NICHT_ZUGEORDNET_LABEL : item.carrier_name ?? gruppe.name ?? item.carrier_uid}</TableCell>
                  <TableCell className="font-mono text-xs">{item.submission_number ?? '-'}</TableCell>
                  <TableCell>{dateTimeShortDe(item.received_at)}</TableCell>
                  <TableCell>{item.version_no ?? '-'}</TableCell>
                  <TableCell>
                    <OffertStatusBadge status={item.status} idpStatus={item.idp_status} isCurrent={item.is_current} versionNo={item.version_no} verworfen={verworfen} />
                  </TableCell>
                  <TableCell>{OFFER_IDP_STATUS_LABELS[item.idp_status]}</TableCell>
                  <TableCell>{item.extractor_confidence !== null ? `${Math.round(item.extractor_confidence * 100)} %` : '-'}</TableCell>
                  <TableCell>{item.normalized?.praemie.jahr != null ? chf(item.normalized.praemie.jahr) : '-'}</TableCell>
                  <TableCell>
                    <a href={`/ablage/${item.document_id}`} target="_blank" rel="noreferrer" className="text-brand-navy hover:underline" onClick={() => onOpen(item)}>
                      oeffnen
                    </a>
                  </TableCell>
                  <TableCell>
                    {showActions && (
                      <div className="flex flex-wrap gap-1">
                        {item.idp_status === OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN && (
                          <Button variant="outline" size="sm" onClick={() => onNormalisieren(item)}>Werte ergaenzen</Button>
                        )}
                        {(unassigned || (!item.is_current && gruppe.zuordenbar)) && (
                          <Button variant="ghost" size="sm" onClick={() => onZuordnen(item)}>Zuordnen</Button>
                        )}
                        {!unassigned && !item.is_current && usable && (
                          <Button variant="ghost" size="sm" onClick={() => onAktuell(item)}>Aktuell</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => onReExtract(item)}>Erneut</Button>
                        <Button variant="ghost" size="sm" onClick={() => onVerwerfen(item)}>Verwerfen</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
